import {
  Alert,
  type AutocompleteInputChangeReason,
  CircularProgress,
  Stack,
  type SxProps,
  type TextFieldProps,
  type Theme,
} from '@mui/material';
import { DROPDOWN_OPTION_PADDING_X, V2ThemeProvider } from '@onevision-resources/design-theme';
import {
  BulkUpdateConfirmationDefinition,
  ConstraintKey,
  getAttributeConfig,
  ValueSource,
  WriteStrategy,
} from '@provision/bulk-update-config';
import { ConversationStatus } from '@provision/conversation-types';
import { EntityBase } from '@provision/entity-types';
import { type KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components';
import {
  type AutoCompleteOption,
  type DefaultOption,
  FormAutocomplete,
  FormTextField,
} from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import {
  type ModalAction,
  ModalContainer,
  ModalList,
  type ModalListItem,
} from 'src/components/ModalContainer';
import { SidePanelCaption } from 'src/components/SidePanelCaption/side-panel-caption';
import { useAddress, useGroupRules, useServiceLevels } from 'src/graphql';
import { Address } from 'src/graphql/entities/address/address';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import {
  BulkUpdateSelectionInput,
  useBulkUpdateEntitiesMutation,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  type BulkCopyActionDefinition,
  type BulkCopyContext,
  type BulkCopyEntity,
  formatBulkCopyLinks,
  formatBulkCopyValues,
  getBulkCopyActions,
} from 'src/graphql/entities/custom-view/custom-view-config/bulk-copy-config';
import {
  type BulkActionContextValues,
  getBulkUpdateActions,
  mapCustomViewEntityToEntityName,
} from 'src/graphql/entities/custom-view/custom-view-config/bulk-update-config';
import { CustomViewEntity } from 'src/graphql/entities/custom-view/custom-view-config/custom-view-config';
import { getBulkEditorCapabilities } from 'src/pages/CustomViews/components/bulk-actions-modal.editor-config';
import { useBulkActionOptions } from 'src/pages/CustomViews/components/bulk-actions-modal.options';
import {
  BulkEditorFormField,
  type BulkEditorFormValues,
  useBulkActionsModalState,
} from 'src/pages/CustomViews/components/bulk-actions-modal.state';
import { useBulkActionSubmit } from 'src/pages/CustomViews/components/bulk-actions-modal.submit';
import {
  BulkOption,
  getBulkOptionLabel,
  getBulkOptionSubmitValue,
} from 'src/pages/CustomViews/components/bulk-actions-modal.utils';
import {
  BulkActionsPicker,
  BulkActionsRootStep,
} from 'src/pages/CustomViews/components/bulk-actions-picker';
import {
  BulkEditorStepContext,
  type BulkEditorStepValue,
} from 'src/pages/CustomViews/components/bulk-editor-step-context';
import {
  hasBulkUpdateActionAccess,
  shouldShowBulkUpdateAction,
} from 'src/pages/CustomViews/utils';
import {
  SupportedActionQueries,
  useOptionsQueries,
} from 'src/pages/Settings/rules/use-rules-engine';
import { useConfirmationModal, useCopyToClipboard, useNotifications } from 'src/utils/hooks';
import { formatCount } from 'src/utils/number';

interface BulkActionsModalProps {
  open: boolean;
  onClose: () => void;
  onBulkUpdateSubmitted: (jobId: string, operationKind?: 'update' | 'delete') => void;
  onBulkActionCompleted?: () => void;
  entity: CustomViewEntity;
  records: EntityBase[];
  totalCount: number;
  selectedCount: number;
  partnerId: number | null;
  selectionInput: BulkUpdateSelectionInput;
  getEntityUrl: (entityId: string) => string;
  asyncActions?: BulkActionsModalAction[];
  initialUpdateAttribute?: string | null;
  canBulkEdit?: boolean;
  canBulkDelete?: boolean;
  /** Values supplied by the hosting page for actions that take their value from context. */
  contextValues?: BulkActionContextValues;
}

export type BulkActionsModalAction = {
  id: string;
  label: string;
  icon: string;
  /** Picker group; a host action without one lists under Copy. */
  group?: BulkActionGroup;
  handler: () => Promise<void> | void;
  isLoading?: boolean;
  isAllowed?: boolean;
};

export type BulkActionGroup = 'Copy' | 'Update' | 'Labels' | 'Delete';

type BulkModalAction = { group: BulkActionGroup } & (
  | {
      id: string;
      label: string;
      icon: string;
      isLoading?: boolean;
      kind: 'copy';
      copyAction: BulkCopyActionDefinition;
    }
  | {
      id: string;
      label: string;
      icon: string;
      isLoading?: boolean;
      kind: 'update-attribute';
      attributeKey: string;
    }
  | {
      id: string;
      label: string;
      icon: string;
      isLoading?: boolean;
      kind: 'delete';
      attributeKey: string;
    }
  | {
      id: string;
      label: string;
      icon: string;
      kind: 'async';
      isLoading: boolean;
      handler: () => Promise<void> | void;
    }
);

type BulkActionPickerItem = ModalListItem & { originalAction: BulkModalAction };

type BulkOptionListItem = ModalListItem & { option: BulkOption };

type BulkUpdateAction = ReturnType<typeof getBulkUpdateActions>[number];

const VALUE_FIELD_LABEL = 'Value';

const TEXT_VALUE_FIELD_PROPS: Partial<TextFieldProps> = {
  inputProps: { 'data-testid': 'bulk-actions-value' },
};

const DATE_VALUE_FIELD_PROPS: Partial<TextFieldProps> = {
  ...TEXT_VALUE_FIELD_PROPS,
  type: 'date',
  slotProps: { inputLabel: { shrink: true } },
};

const SNOOZE_UNTIL_FIELD_PROPS: Partial<TextFieldProps> = {
  type: 'datetime-local',
  slotProps: { inputLabel: { shrink: true } },
};

const ENTITY_SEARCH_FIELD_PROPS: TextFieldProps = { placeholder: 'Search options' };

const confirmationWarningSx = { mt: 1.5 };

// Reads as the list's own caption: the group headings' inset, clear of the filter above it.
const selectedCountSx: SxProps<Theme> = {
  display: 'block',
  margin: '0.75rem 0 0.25rem',
  paddingX: DROPDOWN_OPTION_PADDING_X,
};

const EMPTY_ASYNC_ACTIONS: BulkActionsModalAction[] = [];

const BOOLEAN_TRUE_VALUES = new Set(['yes', 'true', '1']);
const BOOLEAN_FALSE_VALUES = new Set(['no', 'false', '0']);

// temp solution, will be reworked soon
const ADDRESS_BOOLEAN_STRING_ATTRIBUTES = new Set(['tosAccepted', 'creditCardProvided']);

const REMOVE_LABELS_ATTRIBUTE = 'removeLabels';

const OPTION_ICONS: Record<string, string> = {
  yes: 'check',
  no: 'close',
};

const getOptionIcon = (option: BulkOption): string => {
  if (option.icon) {
    return option.icon;
  }

  const normalizedOptionValue = String(option.value ?? option.id ?? '').toLowerCase();
  return OPTION_ICONS[normalizedOptionValue] ?? '';
};

// Enter in the entity search either picks the highlighted option (the Autocomplete handles that
// itself) or does nothing; it must never reach the step's form and submit it.
const keepEnterFromSubmitting = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
};

const toAutocompleteOption = (option: BulkOption): AutoCompleteOption => ({
  id: getBulkOptionSubmitValue(option, 'entity-search'),
  label: getBulkOptionLabel(option),
});

const getEditorTitle = (action: BulkUpdateAction | undefined): string => {
  // A host-context action is not deleting the record, so its own label reads better than
  // "Bulk delete <attribute>".
  if (action?.valueSource === ValueSource.HostContext) {
    return action.label;
  }
  if (action?.kind === 'delete') {
    return `Bulk delete ${action.attributeLabel}`;
  }
  return `Bulk update ${action?.attributeLabel}`;
};

export const BulkActionsModal = ({
  open,
  onClose,
  onBulkUpdateSubmitted,
  onBulkActionCompleted,
  entity,
  records,
  totalCount,
  selectedCount,
  partnerId,
  selectionInput,
  getEntityUrl,
  asyncActions = EMPTY_ASYNC_ACTIONS,
  initialUpdateAttribute = null,
  canBulkEdit = true,
  canBulkDelete = false,
  contextValues,
}: BulkActionsModalProps) => {
  const [confirmationWarning, setConfirmationWarning] = useState<string | null>(null);

  const { confirm, renderConfirmationBody } = useConfirmationModal();
  const { copyToClipboard } = useCopyToClipboard({ alertOptions: { autoHide: 1000 } });
  const copyContext = useMemo<BulkCopyContext>(() => ({ getEntityUrl }), [getEntityUrl]);
  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { groupRules } = useGroupRules();
  const [initBulkUpdate, { loading: isSubmitInProgress }] = useBulkUpdateEntitiesMutation();
  const { associateClientsWithAddress } = useAddress();

  const bulkCopyActions = useMemo(
    () => getBulkCopyActions(entity as BulkCopyEntity),
    [entity],
  );

  const entityName = useMemo(() => mapCustomViewEntityToEntityName(entity), [entity]);

  const bulkUpdateActions = useMemo(
    () =>
      getBulkUpdateActions(entity, { isMultitenancy: isNullish(partnerId), contextValues }),
    [entity, partnerId, contextValues],
  );
  const availableBulkUpdateAttributes = useMemo(
    () => bulkUpdateActions.map((action) => action.attributeKey),
    [bulkUpdateActions],
  );
  const {
    step,
    selectedAttribute,
    formMethods,
    editorValue,
    setEditorValue,
    multiSelectValues,
    setMultiSelectValues,
    searchInputValue,
    setSearchInputValue,
    snoozeUntilValue,
    setSnoozeUntilValue,
    openUpdateConfig,
    resetToActions,
  } = useBulkActionsModalState({
    open,
    initialUpdateAttribute,
    availableAttributes: availableBulkUpdateAttributes,
  });

  const enabledQueries = useMemo(() => {
    return new Set(
      bulkUpdateActions
        .map((action): SupportedActionQueries | undefined => action.optionsQuery)
        .filter(Boolean) as SupportedActionQueries[],
    );
  }, [bulkUpdateActions]);

  const queryOptions = useOptionsQueries({
    partnerId,
    enabledQueries,
    area: 'custom_views',
    concreteCurrentUserOption: true,
  });

  const { serviceLevelOptions, hasServiceLevelAccess } = useServiceLevels(partnerId);

  const actions = useMemo<BulkModalAction[]>(() => {
    const copyActions = bulkCopyActions.map(
      (action): BulkModalAction => ({
        id: action.id,
        label: action.label,
        icon: action.icon,
        isLoading: false,
        kind: 'copy' as const,
        copyAction: action,
        group: 'Copy',
      }),
    );

    const updateActions = canBulkEdit
      ? bulkUpdateActions
          .filter(
            (action) =>
              action.kind !== 'delete' &&
              shouldShowBulkUpdateAction(action, groupRules) &&
              (hasServiceLevelAccess ||
                getAttributeConfig(entityName, action.attributeKey)?.validation
                  .constraintKey !== ConstraintKey.ServiceLevelBrand),
          )
          .map(
            (action): BulkModalAction => ({
              id: action.id,
              label: action.label,
              icon: action.icon,
              isLoading: false,
              kind: 'update-attribute',
              attributeKey: action.attributeKey,
              group:
                getAttributeConfig(entityName, action.updateAttribute)?.execution
                  .writeStrategy === WriteStrategy.LabelMutation
                  ? 'Labels'
                  : 'Update',
            }),
          )
      : [];

    const deleteActions = canBulkDelete
      ? bulkUpdateActions
          .filter(
            (action) =>
              action.kind === 'delete' && hasBulkUpdateActionAccess(action, groupRules),
          )
          .map(
            (action): BulkModalAction => ({
              id: action.id,
              label: action.label,
              icon: action.icon,
              isLoading: false,
              kind: 'delete',
              attributeKey: action.attributeKey,
              group: 'Delete',
            }),
          )
      : [];

    const asyncActionsList =
      asyncActions
        ?.filter((action) => action.isAllowed)
        .map(
          (action): BulkModalAction => ({
            id: action.id,
            label: action.label,
            icon: action.icon,
            kind: 'async',
            isLoading: Boolean(action.isLoading),
            handler: action.handler,
            group: action.group ?? 'Copy',
          }),
        ) ?? [];

    const labelActions = updateActions.filter((action) => action.group === 'Labels');
    const attributeActions = updateActions.filter((action) => action.group !== 'Labels');

    return [
      ...copyActions,
      ...asyncActionsList,
      ...attributeActions,
      ...labelActions,
      ...deleteActions,
    ];
  }, [
    bulkCopyActions,
    bulkUpdateActions,
    asyncActions,
    canBulkEdit,
    canBulkDelete,
    groupRules,
    hasServiceLevelAccess,
    entityName,
  ]);

  const pickerActions = useMemo<BulkActionPickerItem[]>(
    () =>
      actions.map((action) => ({
        id: action.id,
        title: action.label,
        description: `${selectedCount} items`,
        group: action.group,
        icon: action.isLoading ? (
          <CircularProgress size="0.875rem" color="inherit" />
        ) : (
          <DynamicIcon name={action.icon} library="lucide" />
        ),
        originalAction: action,
      })),
    [actions, selectedCount],
  );

  const selectedBulkAction = useMemo(
    () => bulkUpdateActions.find((action) => action.attributeKey === selectedAttribute),
    [bulkUpdateActions, selectedAttribute],
  );
  const isDeleteAction = selectedBulkAction?.kind === 'delete';

  const selectedAttrConfig = useMemo(() => {
    if (!selectedBulkAction?.updateAttribute) return undefined;
    return getAttributeConfig(entityName, selectedBulkAction.updateAttribute);
  }, [entityName, selectedBulkAction?.updateAttribute]);

  const isLabelMutationAction =
    selectedAttrConfig?.execution.writeStrategy === WriteStrategy.LabelMutation;

  const isServiceLevelBulkUpdate = useMemo(
    () => selectedAttrConfig?.validation.constraintKey === ConstraintKey.ServiceLevelBrand,
    [selectedAttrConfig],
  );

  const selectedSiteBrandIds = useMemo(() => {
    if (!isServiceLevelBulkUpdate || entityName !== 'Address') {
      return [];
    }

    return [
      ...new Set(records.map((record) => String((record as Partial<Address>)?.brandId || ''))),
    ].filter(Boolean);
  }, [entityName, isServiceLevelBulkUpdate, records]);

  const { selectedBulkOptions, selectedBulkQueryValue, selectedBulkAutocompleteOptions } =
    useBulkActionOptions({
      entityName,
      records,
      selectedBulkAction,
      selectedAttrConfig,
      queryOptions: queryOptions as Record<
        string,
        {
          options?: BulkOption[];
          historicalOptions?: BulkOption[];
          isLoading?: boolean;
          onInputChange?: (value: string) => void;
        }
      >,
      groupRules,
      isServiceLevelBulkUpdate,
      constrainedServiceLevelOptions: serviceLevelOptions as BulkOption[],
      partnerId,
    });

  const anySelectedSiteHasSubscription = useMemo(() => {
    if (entityName !== 'Address') {
      return false;
    }

    return records.some((record) =>
      Boolean(getCorrectSubscriptionIdFromAddress(record as Address)),
    );
  }, [entityName, records]);

  const hasSubscriptionInSelectedSites =
    isServiceLevelBulkUpdate && anySelectedSiteHasSubscription;

  const requiresSnoozeUntilForValue = useCallback(
    (value: string): boolean => {
      const hasSnoozeMetadata =
        selectedAttrConfig?.execution.metadata?.fields.includes('snoozeUntil');

      return Boolean(
        hasSnoozeMetadata && value.toLowerCase() === ConversationStatus.Snoozed.toLowerCase(),
      );
    },
    [selectedAttrConfig],
  );

  const requiresSnoozeUntil = useMemo(
    () => requiresSnoozeUntilForValue(editorValue),
    [editorValue, requiresSnoozeUntilForValue],
  );

  const handleClose = useCallback(() => {
    resetToActions();
    onClose();
  }, [onClose, resetToActions]);

  const normalizeEditorValue = useCallback(
    (rawValue: string): string | boolean => {
      if (selectedBulkAction?.editorType === 'boolean') {
        const normalized = rawValue.trim().toLowerCase();

        const needsYesNoString =
          entityName === 'Address' &&
          ADDRESS_BOOLEAN_STRING_ATTRIBUTES.has(
            String(selectedBulkAction.updateAttribute ?? ''),
          );

        let normalizedAsYesNo: 'yes' | 'no';
        if (BOOLEAN_TRUE_VALUES.has(normalized)) {
          normalizedAsYesNo = 'yes';
        } else if (BOOLEAN_FALSE_VALUES.has(normalized)) {
          normalizedAsYesNo = 'no';
        } else {
          normalizedAsYesNo = rawValue === 'Yes' ? 'yes' : 'no';
        }

        if (needsYesNoString) {
          return normalizedAsYesNo;
        }

        return normalizedAsYesNo === 'yes';
      }

      if (selectedBulkAction?.editorType === 'entity-search') {
        const normalizedRawValue = String(rawValue);
        const matchingOption = selectedBulkAutocompleteOptions.find(
          (option) =>
            getBulkOptionSubmitValue(option, selectedBulkAction?.editorType) ===
            normalizedRawValue,
        );

        if (selectedAttrConfig?.editor.optionsSource === 'getConversationParticipantOptions') {
          return String(
            matchingOption?.conversationParticipantId ?? matchingOption?.id ?? rawValue,
          );
        }

        return String(matchingOption?.id ?? rawValue);
      }
      if (selectedBulkAction?.editorType === 'date' && rawValue) {
        return rawValue;
      }

      if (
        entityName === 'Address' &&
        String(selectedBulkAction?.updateAttribute ?? '') === 'clientActivatedMembership'
      ) {
        return rawValue.trim();
      }

      return rawValue;
    },
    [
      entityName,
      selectedAttrConfig,
      selectedBulkAction?.editorType,
      selectedBulkAction?.updateAttribute,
      selectedBulkAutocompleteOptions,
    ],
  );

  const { handleSubmitBulkUpdate, handleSubmitBulkDelete } = useBulkActionSubmit({
    isSubmitInProgress,
    selectedBulkAction,
    selectedAttrConfig,
    selectedBulkOptions,
    editorValue,
    multiSelectValues,
    snoozeUntilValue,
    requiresSnoozeUntilForValue,
    isLabelMutationAction,
    selectedCount,
    isServiceLevelBulkUpdate,
    selectedSiteBrandIds,
    hasSubscriptionInSelectedSites,
    records,
    associateClientsWithAddress,
    normalizeEditorValue,
    initBulkUpdate: initBulkUpdate as unknown as (
      args: unknown,
    ) => Promise<{ data?: { bulkUpdateEntities?: { jobId?: string } } }>,
    partnerId,
    entityName,
    selectionInput,
    showErrorAlert,
    showSuccessAlert,
    onBulkActionCompleted,
    onBulkUpdateSubmitted,
    handleClose,
  });

  const requestConfirmation = useCallback(
    async ({
      confirmation,
      danger,
      onConfirm,
    }: {
      confirmation: BulkUpdateConfirmationDefinition;
      danger: boolean;
      onConfirm: () => Promise<void> | void;
    }) => {
      setConfirmationWarning(confirmation.warning ?? null);
      const isConfirmed = await confirm({
        title: confirmation.title,
        body: confirmation.getDescription(selectedCount, totalCount),
        confirmLabel: confirmation.confirmButtonTitle,
        cancelLabel: confirmation.cancelButtonTitle,
        danger,
      });
      setConfirmationWarning(null);

      if (isConfirmed) {
        await onConfirm();
      }
    },
    [confirm, selectedCount, totalCount],
  );

  const submitBulkDeleteActionWithConfirmation = useCallback(
    async (attributeKey: string) => {
      const deleteAction = bulkUpdateActions.find(
        (action) => action.kind === 'delete' && action.attributeKey === attributeKey,
      );
      if (!deleteAction) {
        return;
      }

      const isHostContextAction = deleteAction.valueSource === ValueSource.HostContext;

      // Only actually deleting a site is blocked by billing; a host-context action such as
      // removing a client from a site leaves the site itself untouched.
      if (!isHostContextAction && anySelectedSiteHasSubscription) {
        showErrorAlert(
          "Some selected sites have an active subscription and can't be deleted. Remove them from the selection (or cancel the subscription in billing) and try again.",
        );
        return;
      }

      // A host-context action takes its value from the page rather than an editor.
      const value = isHostContextAction ? deleteAction.contextValue : undefined;
      // It shares the delete submit path but does not delete the records, so the selected rows must
      // not be evicted from the cache and the banner must not report them as deleted.
      const operationKind = isHostContextAction ? ('update' as const) : ('delete' as const);

      const confirmation = deleteAction.confirmation;

      if (!confirmation?.title) {
        await handleSubmitBulkDelete({ attributeKey, value, operationKind });
        return;
      }

      await requestConfirmation({
        confirmation,
        danger: (confirmation.confirmButtonColor ?? 'error') === 'error',
        onConfirm: () =>
          handleSubmitBulkDelete({
            attributeKey,
            value,
            operationKind,
          }),
      });
    },
    [
      bulkUpdateActions,
      handleSubmitBulkDelete,
      anySelectedSiteHasSubscription,
      requestConfirmation,
      showErrorAlert,
    ],
  );

  const submitBulkUpdateWithConfirmation = useCallback(
    async ({
      overrideEditorValue,
      overrideSnoozeUntilValue,
      overrideMultiSelectValues,
    }: {
      overrideEditorValue?: string;
      overrideSnoozeUntilValue?: string;
      overrideMultiSelectValues?: string[];
    } = {}) => {
      if (!selectedBulkAction || selectedBulkAction.kind !== 'update') {
        return;
      }

      const confirmation = selectedBulkAction.confirmation;

      if (!confirmation?.title) {
        await handleSubmitBulkUpdate({
          overrideEditorValue,
          overrideSnoozeUntilValue,
          overrideMultiSelectValues,
        });
        return;
      }

      await requestConfirmation({
        confirmation,
        danger: false,
        onConfirm: () =>
          handleSubmitBulkUpdate({
            overrideEditorValue,
            overrideSnoozeUntilValue,
            overrideMultiSelectValues,
          }),
      });
    },
    [handleSubmitBulkUpdate, requestConfirmation, selectedBulkAction],
  );

  const submitBulkDeleteWithConfirmation = useCallback(async () => {
    if (!selectedBulkAction || selectedBulkAction.kind !== 'delete') {
      return;
    }
    await submitBulkDeleteActionWithConfirmation(selectedBulkAction.attributeKey);
  }, [selectedBulkAction, submitBulkDeleteActionWithConfirmation]);

  const handleActionClick = useCallback(
    async (action: BulkModalAction) => {
      if (action.kind === 'update-attribute') {
        openUpdateConfig(action.attributeKey);
        return;
      }

      if (action.kind === 'delete') {
        await submitBulkDeleteActionWithConfirmation(action.attributeKey);
        return;
      }

      if (action.kind === 'async') {
        await action.handler();
        onClose();
        return;
      }

      const copyAction = action.copyAction;
      const values = records.map((record) => copyAction.resolver(record, copyContext));

      if (copyAction.isFriendlyLink) {
        await copyToClipboard(formatBulkCopyLinks(values));
      } else {
        await copyToClipboard(formatBulkCopyValues(values));
      }

      onClose();
    },
    [
      copyContext,
      copyToClipboard,
      onClose,
      openUpdateConfig,
      records,
      submitBulkDeleteActionWithConfirmation,
    ],
  );

  const handleOptionClick = useCallback(
    async (option: BulkOption) => {
      if (!selectedBulkAction || isSubmitInProgress) {
        return;
      }

      const nextEditorValue = getBulkOptionSubmitValue(option, selectedBulkAction.editorType);
      const isMultiSelectEditor = selectedBulkAction.editorType === 'multi-select';

      if (isMultiSelectEditor) {
        setMultiSelectValues((current) =>
          current.includes(nextEditorValue)
            ? current.filter((value) => value !== nextEditorValue)
            : [...current, nextEditorValue],
        );
        return;
      }

      setEditorValue(nextEditorValue);

      if (requiresSnoozeUntilForValue(nextEditorValue)) {
        setSnoozeUntilValue('');
        return;
      }

      await submitBulkUpdateWithConfirmation({ overrideEditorValue: nextEditorValue });
    },
    [
      selectedBulkAction,
      isSubmitInProgress,
      requiresSnoozeUntilForValue,
      submitBulkUpdateWithConfirmation,
      setEditorValue,
      setMultiSelectValues,
      setSnoozeUntilValue,
    ],
  );

  const editorCapabilities = useMemo(
    () => getBulkEditorCapabilities(selectedBulkAction?.editorType),
    [selectedBulkAction?.editorType],
  );
  const isOptionListEditor = editorCapabilities.isOptionList;
  const needsManualSubmit = editorCapabilities.needsManualSubmit || requiresSnoozeUntil;

  const isSaveDisabled = useMemo(() => {
    let isValueMissing = false;
    if (!isDeleteAction) {
      isValueMissing =
        selectedBulkAction?.editorType === 'multi-select'
          ? multiSelectValues.length === 0
          : !editorValue;
    }

    return (
      !selectedBulkAction ||
      isValueMissing ||
      (requiresSnoozeUntil && !snoozeUntilValue) ||
      isSubmitInProgress
    );
  }, [
    isDeleteAction,
    selectedBulkAction,
    editorValue,
    multiSelectValues,
    requiresSnoozeUntil,
    snoozeUntilValue,
    isSubmitInProgress,
  ]);
  let saveButtonLabel = 'Save';
  if (isDeleteAction) {
    saveButtonLabel = 'Continue';
  }
  if (isLabelMutationAction && multiSelectValues.length > 0) {
    const verb =
      selectedBulkAction?.updateAttribute === REMOVE_LABELS_ATTRIBUTE ? 'Remove' : 'Add';
    const count = multiSelectValues.length;
    saveButtonLabel = `${verb} ${count} label${count === 1 ? '' : 's'}`;
  }
  if (isSubmitInProgress) {
    saveButtonLabel = 'Submitting...';
  }

  const handlePickerSelect = useCallback(
    (item: BulkActionPickerItem) => {
      void handleActionClick(item.originalAction);
    },
    [handleActionClick],
  );

  const optionListItems = useMemo<BulkOptionListItem[]>(
    () =>
      selectedBulkOptions.map((option) => {
        const iconName = getOptionIcon(option);

        return {
          id: String(option.id ?? option.value),
          title: getBulkOptionLabel(option),
          icon: iconName ? (
            <DynamicIcon
              name={iconName}
              library={option.iconLibrary}
              sx={option.color ? { color: option.color } : undefined}
            />
          ) : undefined,
          checked:
            selectedBulkAction?.editorType === 'multi-select'
              ? multiSelectValues.includes(
                  getBulkOptionSubmitValue(option, selectedBulkAction.editorType),
                )
              : undefined,
          disabled: isSubmitInProgress,
          option,
        };
      }),
    [
      isSubmitInProgress,
      multiSelectValues,
      selectedBulkAction?.editorType,
      selectedBulkOptions,
    ],
  );

  const entityOptions = useMemo(
    () => selectedBulkAutocompleteOptions.map(toAutocompleteOption),
    [selectedBulkAutocompleteOptions],
  );

  const handleOptionListSelect = useCallback(
    (item: BulkOptionListItem) => {
      void handleOptionClick(item.option);
    },
    [handleOptionClick],
  );

  const handleEntityInputChange = (
    _event: unknown,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setSearchInputValue(value);
    if (reason === 'input') {
      selectedBulkQueryValue?.onInputChange?.(value);
    }
  };

  const handleEntityOptionChange = (
    _event: unknown,
    value: DefaultOption | DefaultOption[] | null,
  ) => {
    const option = Array.isArray(value) ? null : value;
    setEditorValue(option && typeof option === 'object' ? String(option.id) : '');
  };

  const selectedCountLabel = (
    <SidePanelCaption
      component="p"
      sx={selectedCountSx}
      data-testid="bulk-actions-selected-count"
    >
      {formatCount(selectedCount)} / {formatCount(totalCount)} items selected
    </SidePanelCaption>
  );

  const saveAction: ModalAction = {
    label: saveButtonLabel,
    kind: 'primary',
    onClick: () =>
      void (isDeleteAction
        ? submitBulkDeleteWithConfirmation()
        : submitBulkUpdateWithConfirmation()),
    disabled: isSaveDisabled,
    testId: 'bulk-actions-save',
  };

  // The pushed editor step has to re-render with every render of the modal; it owns no other subtree.
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const editorStep: BulkEditorStepValue = {
    isOpen: step === 'update-config',
    title: getEditorTitle(selectedBulkAction),
    primaryAction: needsManualSubmit ? saveAction : undefined,
    children: (
      <GenericForm<BulkEditorFormValues> formMethods={formMethods}>
        {/* The field and its count line stay one block, so the form's gap does not widen the space between them. */}
        {isOptionListEditor ? null : (
          <Stack>
            {editorCapabilities.renderTextInput && !isDeleteAction ? (
              <FormTextField
                type="textfield"
                name={BulkEditorFormField.Value}
                label={VALUE_FIELD_LABEL}
                textFieldProps={TEXT_VALUE_FIELD_PROPS}
              />
            ) : null}

            {editorCapabilities.renderDateInput && !isDeleteAction ? (
              <FormTextField
                type="textfield"
                name={BulkEditorFormField.Value}
                label={VALUE_FIELD_LABEL}
                textFieldProps={DATE_VALUE_FIELD_PROPS}
              />
            ) : null}

            {editorCapabilities.renderEntitySearch && !isDeleteAction ? (
              <FormAutocomplete<AutoCompleteOption>
                type="autocomplete"
                name={BulkEditorFormField.EntityOption}
                label={VALUE_FIELD_LABEL}
                data-testid="bulk-actions-entity-search"
                options={entityOptions}
                loading={Boolean(selectedBulkQueryValue?.isLoading)}
                textFieldProps={ENTITY_SEARCH_FIELD_PROPS}
                autocompleteProps={{
                  inputValue: searchInputValue,
                  onInputChange: handleEntityInputChange,
                  onChange: handleEntityOptionChange,
                  onKeyDown: keepEnterFromSubmitting,
                }}
              />
            ) : null}

            {selectedCountLabel}
          </Stack>
        )}

        {selectedBulkAction && isOptionListEditor && !isDeleteAction ? (
          <ModalList
            items={optionListItems}
            onSelect={handleOptionListSelect}
            belowFilter={selectedCountLabel}
            testId="bulk-actions-option"
          />
        ) : null}

        {requiresSnoozeUntil ? (
          <FormTextField
            type="textfield"
            name={BulkEditorFormField.SnoozeUntil}
            label="Snooze Until"
            textFieldProps={SNOOZE_UNTIL_FIELD_PROPS}
          />
        ) : null}
      </GenericForm>
    ),
  };

  return (
    <>
      {confirmationWarning
        ? renderConfirmationBody(
            <V2ThemeProvider>
              <Alert severity="warning" sx={confirmationWarningSx}>
                {confirmationWarning}
              </Alert>
            </V2ThemeProvider>,
          )
        : null}

      <BulkEditorStepContext.Provider value={editorStep}>
        <ModalContainer open={open} size="medium" onClose={handleClose}>
          <BulkActionsRootStep
            isEditorOpen={step === 'update-config'}
            onEditorClosed={resetToActions}
          >
            <BulkActionsPicker options={pickerActions} onSelect={handlePickerSelect} />
          </BulkActionsRootStep>
        </ModalContainer>
      </BulkEditorStepContext.Provider>
    </>
  );
};
